import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  TextField,
  Divider,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [savingImage, setSavingImage] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const theme = useTheme();

  // Carregar dados do usuário
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/users/me');
        setUser(response.data);
        
        // Verificar se o usuário já tem uma URL de imagem definida
        if (response.data && response.data.profile_image_url) {
          setProfileImageUrl(response.data.profile_image_url);
          setAvatarPreview(response.data.profile_image_url);
        } else {
          // Usar o placeholder de avatar com iniciais
          setAvatarPreview(`https://ui-avatars.com/api/?name=${response.data.username}&background=random`);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        setNotification({
          open: true,
          message: 'Erro ao carregar dados do perfil',
          severity: 'error'
        });
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Função para testar a URL da imagem
  const handleTestImage = () => {
    if (profileImageUrl) {
      setAvatarPreview(profileImageUrl);
    }
  };

  // Salvar alterações de URL da imagem
  const handleSaveProfileImage = async () => {
    if (!profileImageUrl) {
      setNotification({
        open: true,
        message: 'Por favor, insira uma URL de imagem válida',
        severity: 'warning'
      });
      return;
    }

    try {
      setSavingImage(true);
      
      // Chamar a API para atualizar a URL da imagem do usuário
      const response = await api.put('/users/update-profile-image', {
        profile_image_url: profileImageUrl
      });
      
      // Atualizar o estado local com os dados atualizados
      setUser({
        ...user,
        profile_image_url: profileImageUrl
      });
      
      setNotification({
        open: true,
        message: 'URL da imagem de perfil atualizada com sucesso!',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Erro ao atualizar URL da imagem de perfil:', error);
      setNotification({
        open: true,
        message: `Erro ao atualizar perfil: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    } finally {
      setSavingImage(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  const getRoleName = (roleId) => {
    switch (roleId) {
      case 1:
        return 'Administrador';
      case 2:
        return 'Usuário';
      case 3:
        return 'Técnico';
      default:
        return 'Desconhecido';
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Não foi possível carregar dados do usuário</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4, px: { xs: 2, md: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, bgcolor: theme.palette.background.paper }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1, display: 'inline-block' }}>
          Meu Perfil
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Grid container spacing={4}>
          {/* Coluna da esquerda - Avatar e configurações de imagem */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                bgcolor: theme.palette.background.paper, 
                p: 2, 
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                mb: 3,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Avatar
                src={avatarPreview}
                alt={user.username}
                sx={{ 
                  width: 150, 
                  height: 150, 
                  mb: 2,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
                }}
              >
                {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {user.username}
              </Typography>
              
              <Chip 
                label={getRoleName(user.role_id)} 
                color={user.role_id === 1 ? "primary" : user.role_id === 3 ? "info" : "default"}
                sx={{ mb: 2 }}
              />
            </Box>
            
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                width: '100%',
                borderRadius: '8px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Alterar Foto de Perfil
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Insira a URL da imagem que deseja usar como sua foto de perfil.
              </Typography>
              
              <TextField
                fullWidth
                label="URL da Imagem"
                variant="outlined"
                value={profileImageUrl}
                onChange={(e) => setProfileImageUrl(e.target.value)}
                margin="normal"
                placeholder="https://exemplo.com/minha-imagem.jpg"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleTestImage}
                  disabled={!profileImageUrl}
                  startIcon={<PhotoCamera />}
                >
                  Visualizar
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveProfileImage}
                  disabled={!profileImageUrl || savingImage}
                  startIcon={savingImage ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {savingImage ? 'Salvando...' : 'Salvar Foto'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Coluna da direita - Informações do usuário */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: '8px',
                height: '100%'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 3, borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 }}>
                Informações da Conta
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Nome de Usuário
                    </Typography>
                    <Typography variant="h6">
                      {user.username}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Tipo de Perfil
                    </Typography>
                    <Typography variant="h6">
                      {getRoleName(user.role_id)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Status da Conta
                    </Typography>
                    <Typography variant="h6">
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Data de Criação
                    </Typography>
                    <Typography variant="h6">
                      {formatDate(user.created_at)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Último Acesso
                    </Typography>
                    <Typography variant="h6">
                      {user.last_login ? formatDate(user.last_login) : 'Primeiro acesso'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile; 